import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { Center, Spinner } from "native-base";
import { v4 as uuidv4 } from "uuid";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import axios from "axios";
import qs from "qs";
import * as analytics from "firebase/analytics";
import Hotjar from "@hotjar/browser";
import constants from "./constants";
import {
  initialState,
  initialStatus,
  StateContext,
  stateReducer,
  StatusContext,
  statusReducer,
} from "./contexts";
import API, { getErrorMessage } from "./api";
import routes from "./routes";
import Toast from "./components/toast";
import colors from "./constants/colors";
import ErrorBoundary from "./components/errorBoundary";
import RouteFallback from "./components/routeFallback";

import { useTranslation } from "react-i18next";
import moment from "moment";
import { setDefaultLocale } from "react-datepicker";
import { postToParent } from "./utils";
import { syncAnalyticsStack, trackPageView } from "./services/analytics";

// TODO import AgencyDashboardPage from "./pages/agency/dashboard";

const MainLayout = React.lazy(() => import("./layouts/main"));
const SignUpLayout = React.lazy(() => import("./layouts/signUp"));
const VendorLayout = React.lazy(() => import("./layouts/vendor"));
const ProfileLayout = React.lazy(() => import("./layouts/profile"));
const GuidedTourLayout = React.lazy(() => import("./layouts/guidedTour"));
const TransferLayout = React.lazy(() => import("./layouts/transfer"));

const HomePage = React.lazy(() => import("./pages/home"));
const SignUpVendorPage = React.lazy(() => import("./pages/signUp/vendor"));
const SignUpAgencyRntPage = React.lazy(() =>
  import("./pages/signUp/agency/rnt"),
);
const SignUpAgencyDataPage = React.lazy(() =>
  import("./pages/signUp/agency/data"),
);
const SignUpAgencyDocumentsPage = React.lazy(() =>
  import("./pages/signUp/agency/documents"),
);
const SignUpAgencyPendingPage = React.lazy(() =>
  import("./pages/signUp/agency/pending"),
);
const SignUpAgencySuccessPage = React.lazy(() =>
  import("./pages/signUp/agency/success"),
);
const SignUpAgencyPage = React.lazy(() => import("./pages/signUp/agency"));
const SignInPage = React.lazy(() => import("./pages/signIn"));

const AgencyExperienceCreationPage = React.lazy(() =>
  import("./pages/agency/experienceCreation"),
);
const AgencyExperienceCreationPurchaseSummaryPage = React.lazy(() =>
  import("./pages/agency/experienceCreation/purchaseSummary"),
);
const AgencyPaymentManagementPage = React.lazy(() =>
  import("./pages/agency/paymentManagement"),
);
const AgencyPaymentManagementInstallmentsPage = React.lazy(() =>
  import("./pages/agency/paymentManagement/installments"),
);
const AgencyPaymentManagementPricingPage = React.lazy(() =>
  import("./pages/agency/paymentManagement/pricing"),
);
const AgencyPaymentManagementPassengersInstallmentsPage = React.lazy(() =>
  import("./pages/agency/paymentManagement/passengersInstallments"),
);
const AgencyProfilePage = React.lazy(() => import("./pages/agency/profile"));
const AgencyTripsAndPassengersPage = React.lazy(() =>
  import("./pages/agency/tripsAndPassengers"),
);
const AgencyQuotationsPage = React.lazy(() =>
  import("./pages/agency/quotations"),
);
const AgencyProfileBalancePage = React.lazy(() =>
  import("./pages/agency/profile/balance"),
);
const AgencyPaymentManagementServicesPage = React.lazy(() =>
  import("./pages/agency/paymentManagement/services"),
);
const AgencyProfileDocumentsPage = React.lazy(() =>
  import("./pages/agency/profile/documents"),
);
const AgencyPaymentManagementServicesServicePage = React.lazy(() =>
  import("./pages/agency/paymentManagement/services/service"),
);

const TourPage = React.lazy(() => import("./pages/tour"));
const TourSeatSelectionPage = React.lazy(() =>
  import("./pages/tour/seatSelection"),
);
const TourPurchaseSummaryPage = React.lazy(() =>
  import("./pages/tour/purchaseSummary"),
);
const SignUpPage = React.lazy(() => import("./pages/signUp/index.js"));
const TicketsPage = React.lazy(() => import("./pages/tickets"));
const PaymentResultPage = React.lazy(() => import("./pages/payment/result"));
const ExperienceCreationPage = React.lazy(() =>
  import("./pages/experienceCreation"),
);
const ExperienceCreationPurchaseSummaryPage = React.lazy(() =>
  import("./pages/experienceCreation/purchaseSummary"),
);
const QuotationsPage = React.lazy(() => import("./pages/quotations"));
const ProfileBalancePage = React.lazy(() => import("./pages/profile/balance"));
const TripsPage = React.lazy(() => import("./pages/trips"));
const ExperienceCreationComplementsPage = React.lazy(() =>
  import("./pages/experienceCreation/complements"),
);
const TripsInstallmentsPage = React.lazy(() =>
  import("./pages/trips/installments/index.js"),
);
const ForgotPasswordPage = React.lazy(() =>
  import("./pages/forgotPassword/index.js"),
);
const ResetPasswordPage = React.lazy(() =>
  import("./pages/resetPassword/index.js"),
);
const ProfilePage = React.lazy(() => import("./pages/profile"));
const ProfileDocumentsPage = React.lazy(() =>
  import("./pages/profile/documents"),
);
const TripsConfirmationPage = React.lazy(() =>
  import("./pages/trips/confirmation"),
);
const DocsTermsAndConditionsPage = React.lazy(() =>
  import("./pages/docs/termsAndConditions"),
);
const SurveyPage = React.lazy(() => import("./pages/survey"));
const AgencyTemplatesPage = React.lazy(() =>
  import("./pages/agency/templates"),
);
const AgencyTemplatesBookingPage = React.lazy(() =>
  import("./pages/agency/templates/booking"),
);
const TransferPage = React.lazy(() => import("./pages/transfer"));
const TransferTransportSelection = React.lazy(() =>
  import("./pages/transfer/transportSelection"),
);
const TransferScheduleSelection = React.lazy(() =>
  import("./pages/transfer/scheduleSelection"),
);
const TransferBookingConfirmation = React.lazy(() =>
  import("./pages/transfer/bookingConfirmation"),
);
const TransferPaymentConfirmation = React.lazy(() =>
  import("./pages/transfer/paymentConfirmation"),
);
const AgencyCommissionsPage = React.lazy(() =>
  import("./pages/agency/commissions"),
);
const TransferBookingLookup = React.lazy(() =>
  import("./pages/transfer/bookingLookup"),
);
const TransferBookingDetails = React.lazy(() =>
  import("./pages/transfer/bookingDetails"),
);

function AnalyticsManager() {
  const location = useLocation();

  useEffect(() => {
    const { shouldTrackPageView } = syncAnalyticsStack(location.pathname);

    if (!shouldTrackPageView) {
      return;
    }

    const timer = window.setTimeout(() => {
      trackPageView({
        pathname: location.pathname,
        search: location.search,
      });
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, [location.pathname, location.search]);

  return null;
}

const { USER, JWT, IS_LOGGED_IN, IS_WHATSAPP_BUBBLE } = constants.state;
const { ERROR_MESSAGE, SUCCESS_MESSAGE, INFO_MESSAGE, WARNING_MESSAGE } =
  constants.status;

const TRANSFER_HOSTNAME = process.env.REACT_APP_TRANSFER_HOSTNAME;

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [state, dispatchState] = useReducer(stateReducer, initialState);
  const [status, dispatchStatus] = useReducer(statusReducer, initialStatus);
  const { i18n } = useTranslation();
  const isTransferRoute = location.pathname.startsWith(routes.transfer);

  useEffect(() => {
    if (
      TRANSFER_HOSTNAME &&
      window.location.hostname === TRANSFER_HOSTNAME &&
      !isTransferRoute
    ) {
      navigate(
        {
          pathname: routes.transfer,
          search: location.search,
          hash: location.hash,
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    moment.locale(i18n.language);
    setDefaultLocale(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    const lang = searchParams.get("lang");
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRefreshUser = useCallback(
    async (jwt = "") => {
      const { data: user } = await API(jwt || state.jwt).get("/users/me");
      dispatchState({ type: USER, user });
      return user;
    },
    [state.jwt],
  );

  const stateContext = useMemo(
    () => ({
      updateUser: (user) => dispatchState({ type: USER, user }),
      updateJwt: (jwt) => dispatchState({ type: JWT, jwt }),
      updateIsLoggedIn: (isLoggedIn) =>
        dispatchState({ type: IS_LOGGED_IN, isLoggedIn }),
      updateIsWhatsappBubble: (isWhatsappBubble) =>
        dispatchState({ type: IS_WHATSAPP_BUBBLE, isWhatsappBubble }),
      refreshUser: onRefreshUser,
      ...state,
    }),
    [state, onRefreshUser],
  );

  const statusContext = useMemo(
    () => ({
      updateErrorMessage: (errorMessage) => {
        if (!axios.isCancel(errorMessage)) {
          dispatchStatus({
            type: ERROR_MESSAGE,
            errorMessage: getErrorMessage(errorMessage),
          });
        }
      },
      updateSuccessMessage: (successMessage) =>
        dispatchStatus({ type: SUCCESS_MESSAGE, successMessage }),
      updateInfoMessage: (infoMessage) =>
        dispatchStatus({ type: INFO_MESSAGE, infoMessage }),
      updateWarningMessage: (warningMessage) =>
        dispatchStatus({ type: WARNING_MESSAGE, warningMessage }),
    }),
    [],
  );

  const initialize = useCallback(async () => {
    try {
      let uuid = localStorage.getItem(constants.UUID);
      if (!uuid) {
        uuid = uuidv4();
        localStorage.setItem(constants.UUID, uuid);
      }
      if (searchParams.get("jwt") !== null) {
        localStorage.setItem(JWT, searchParams.get("jwt"));
        if (searchParams.get("return_url")) {
          const url = decodeURIComponent(searchParams.get("return_url"));
          window.location.href = url;
          return;
        } else {
          setSearchParams({}, { replace: true });
        }
      }
      const jwt = sessionStorage.getItem(JWT) || localStorage.getItem(JWT);
      if (jwt && window.location.pathname !== routes.resetPassword) {
        const { data: user } = await API(jwt).get("/users/me");
        dispatchState({ type: JWT, jwt });
        dispatchState({ type: USER, user });
        dispatchState({ type: IS_LOGGED_IN, isLoggedIn: true });
        if (
          user?.agency &&
          !window.location.pathname.startsWith(routes.agency)
        ) {
          navigate(routes.agencyExperienceCreation);
        }
      }
    } catch (error) {
      dispatchStatus({
        type: ERROR_MESSAGE,
        errorMessage: getErrorMessage(error),
      });
    }
    document.querySelector("#spinner-container")?.remove();
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (!isLoading) {
      postToParent({ ...state, url: window.location.href });
    }
  }, [isLoading, location, state]);

  useEffect(() => {
    if (state.user && !isTransferRoute) {
      analytics.setUserId(analytics.getAnalytics(), String(state.user.id));
      analytics.setUserProperties(analytics.getAnalytics(), state.user);
      if (Hotjar.isReady()) {
        Hotjar.identify(String(state.user.id), state.user);
      }
    }
  }, [isTransferRoute, state.user]);

  useEffect(() => {
    if (Hotjar.isReady()) {
      Hotjar.stateChange(location.pathname);
    }
  }, [location]);

  if (isLoading) {
    return (
      <Center flex={1} backgroundColor={colors.pageBackground}>
        <Spinner size="lg" color={colors.primary} />
      </Center>
    );
  }

  return (
    <StateContext.Provider value={stateContext}>
      <StatusContext.Provider value={statusContext}>
        <AnalyticsManager />
        <ErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route
                path={routes.resetPassword}
                element={<ResetPasswordPage />}
              />
              <Route path={routes.survey} element={<SurveyPage />} />
              {!state.isLoggedIn && (
                <>
                  <Route path={routes.signIn} element={<SignInPage />} />
                  <Route path="*" element={<Navigate to={routes.signIn} />} />
                  <Route
                    path={routes.home}
                    element={<Navigate to={routes.signIn} />}
                  />
                  <Route path={routes.signInRole} element={<SignInPage />} />
                  <Route
                    path={routes.forgotPassword}
                    element={<ForgotPasswordPage />}
                  />
                  <Route path={routes.signUp} element={<SignUpLayout />}>
                    <Route
                      path={routes.signUpVendor}
                      element={<SignUpVendorPage />}
                    />
                    <Route
                      path={routes.signUpAgency}
                      element={<SignUpAgencyPage />}
                    />
                    <Route index element={<SignUpPage />} />
                  </Route>
                </>
              )}
              <Route path={routes.home} element={<MainLayout />}>
                <Route path={routes.home} element={<HomePage />} />
                <Route path={routes.tour} element={<TourPage />} />
                <Route
                  path={routes.tourSeatSelection}
                  element={<TourSeatSelectionPage />}
                />
                <Route
                  path={routes.tourPurchaseSummary}
                  element={<TourPurchaseSummaryPage />}
                />
                <Route
                  path={routes.paymentResult}
                  element={<PaymentResultPage />}
                />
                <Route
                  path={routes.experienceCreation}
                  element={<ExperienceCreationPage />}
                />
                <Route
                  path={routes.experienceCreationId}
                  element={<ExperienceCreationPage />}
                />
                <Route
                  path={routes.experienceCreationPurchaseSummary}
                  element={<GuidedTourLayout type="purchase" />}>
                  <Route
                    index
                    element={<ExperienceCreationPurchaseSummaryPage />}
                  />
                </Route>
                <Route
                  path={routes.experienceCreationComplements}
                  element={<ExperienceCreationComplementsPage />}
                />
                {state.isLoggedIn && !state.user.isAgency && (
                  <>
                    <Route path={routes.tickets} element={<TicketsPage />} />
                    <Route
                      path={routes.quotations}
                      element={<QuotationsPage />}
                    />
                    <Route path={routes.profile} element={<ProfileLayout />}>
                      <Route
                        path={routes.profileBalance}
                        element={<ProfileBalancePage />}
                      />
                      <Route
                        path={routes.profileDocuments}
                        element={<ProfileDocumentsPage />}
                      />
                      <Route index element={<ProfilePage />} />
                    </Route>
                    <Route path={routes.trips} element={<TripsPage />} />
                    <Route
                      path="/my-trips"
                      element={
                        <Navigate
                          to={{
                            pathname: routes.trips,
                            search: window.location.search,
                            hash: window.location.hash,
                          }}
                        />
                      }
                    />
                    <Route
                      path={routes.tripsInstallments}
                      element={<TripsInstallmentsPage />}
                    />
                    <Route
                      path={routes.tripsConfirmation}
                      element={<TripsConfirmationPage />}
                    />
                  </>
                )}
                <Route
                  path={routes.docsTermsAndConditions}
                  element={<DocsTermsAndConditionsPage />}
                />
              </Route>
              <Route path={routes.transfer} element={<TransferLayout />}>
                <Route path={routes.transfer} element={<TransferPage />} />
                <Route
                  path={routes.transferTransportSelection}
                  element={<TransferTransportSelection />}
                />
                <Route
                  path={routes.transferScheduleSelection}
                  element={<TransferScheduleSelection />}
                />
                <Route
                  path={routes.transferBookingConfirmation}
                  element={<TransferBookingConfirmation />}
                />
                <Route
                  path={routes.transferPaymentConfirmation}
                  element={<TransferPaymentConfirmation />}
                />
                <Route
                  path={routes.transferBookingLookup}
                  element={<TransferBookingLookup />}
                />
                <Route
                  path={routes.transferBookingDetails}
                  element={<TransferBookingDetails />}
                />
              </Route>
              {state.isLoggedIn && state.user.isAgency && (
                <Route path={routes.signUp} element={<SignUpLayout />}>
                  <Route
                    path={routes.signUpAgencyRnt}
                    element={<SignUpAgencyRntPage />}
                  />
                  <Route
                    path={routes.signUpAgencyData}
                    element={<SignUpAgencyDataPage />}
                  />
                  <Route
                    path={routes.signUpAgencyDocuments}
                    element={<SignUpAgencyDocumentsPage />}
                  />
                  <Route
                    path={routes.signUpAgencyPending}
                    element={<SignUpAgencyPendingPage />}
                  />
                  <Route
                    path={routes.signUpAgencySuccess}
                    element={<SignUpAgencySuccessPage />}
                  />
                </Route>
              )}
              {state.isLoggedIn && state.user.isAgency && (
                <Route path={routes.agency} element={<VendorLayout />}>
                  {/* TODO <Route
                path={routes.agencyDashboard}
                element={<AgencyDashboardPage />}
              /> */}
                  <Route
                    path={routes.agencyProfile}
                    element={<AgencyProfilePage />}
                  />
                  <Route
                    path={routes.agencyCommissions}
                    element={<AgencyCommissionsPage />}
                  />
                  <Route
                    path={routes.agencyProfileBalance}
                    element={<AgencyProfileBalancePage />}
                  />
                  <Route
                    path={routes.agencyProfileDocuments}
                    element={<AgencyProfileDocumentsPage />}
                  />
                  <Route
                    path={routes.agencyExperienceCreation}
                    element={<AgencyExperienceCreationPage />}
                  />
                  <Route
                    path={routes.agencyExperienceCreationId}
                    element={<AgencyExperienceCreationPage />}
                  />
                  <Route
                    path={routes.agencyExperienceCreationPurchaseSummary}
                    element={<AgencyExperienceCreationPurchaseSummaryPage />}
                  />
                  <Route
                    path={routes.agencyPaymentManagement}
                    element={<AgencyPaymentManagementPage />}
                  />
                  <Route
                    path={routes.agencyPaymentManagementPricing}
                    element={<AgencyPaymentManagementPricingPage />}
                  />
                  <Route
                    path={routes.agencyPaymentManagementInstallments}
                    element={<AgencyPaymentManagementInstallmentsPage />}
                  />
                  <Route
                    path={routes.agencyPaymentManagementServices}
                    element={<AgencyPaymentManagementServicesPage />}
                  />
                  <Route
                    path={routes.agencyPaymentManagementServicesService}
                    element={<AgencyPaymentManagementServicesServicePage />}
                  />
                  <Route
                    path={routes.agencyPaymentManagementPassengersInstallments}
                    element={
                      <AgencyPaymentManagementPassengersInstallmentsPage />
                    }
                  />
                  <Route
                    path={routes.agencyTripsAndPassengers}
                    element={<AgencyTripsAndPassengersPage />}
                  />
                  <Route
                    path={routes.agencyQuotations}
                    element={<AgencyQuotationsPage />}
                  />
                  <Route
                    path={routes.agencyTemplates}
                    element={<AgencyTemplatesPage />}
                  />
                  <Route
                    path={routes.agencyTemplatesBooking}
                    element={<AgencyTemplatesBookingPage />}
                  />
                  <Route
                    index
                    element={<Navigate to={routes.agencyExperienceCreation} />}
                  />
                </Route>
              )}
              {state.isLoggedIn ? (
                state.user.isAgency ? (
                  <Route
                    path="*"
                    element={<Navigate to={routes.agencyExperienceCreation} />}
                  />
                ) : (
                  <Route
                    path="*"
                    element={<Navigate to={routes.experienceCreation} />}
                  />
                )
              ) : (
                <Route
                  path="*"
                  element={
                    <Navigate
                      to={{
                        pathname: routes.signIn,
                        search: qs.stringify({ url: location.pathname }),
                      }}
                    />
                  }
                />
              )}
            </Routes>
          </Suspense>
        </ErrorBoundary>
        <Toast status={{ ...statusContext, ...status }} />
      </StatusContext.Provider>
    </StateContext.Provider>
  );
}

export default App;
